import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import type { DoseStatus } from '@rondela/shared'
import { useAuthStore } from '../../auth/store'
import { usePetsStore } from '../../pets/store'
import { useMedicationsStore } from '../../medications/store'
import { useCheckupsStore } from '../../checkups/store'
import { useVaccinesStore } from '../../vaccines/store'
import { useDashboardStore, type DashboardSortOption } from '../store'
import { aggregatePetActions } from '../lib/urgency'
import { ResponsiveLayout } from '../../../shared/components/layout'
import { MasterDetailLayout } from '../../../shared/components/layout'
import { ExpandableCardList } from '../../../shared/components/layout'
import { useIsDesktop } from '../../../shared/hooks'
import { EmptyState } from '../../../shared/components/EmptyState'
import { PetTaskCard } from '../components/PetTaskCard'
import { PetListCard } from '../components/PetListCard'
import { PetDetailPanel } from '../components/PetDetailPanel'

const SORT_OPTIONS: { value: DashboardSortOption; label: string }[] = [
  { value: 'urgency', label: 'Urgencia' },
  { value: 'medications', label: 'Medicamentos' },
  { value: 'appointments', label: 'Citas' },
  { value: 'alphabetical', label: 'Alfabético' },
]

export function DashboardPage() {
  const { user } = useAuthStore()
  const { pets, fetchPets } = usePetsStore()
  const { pendingDoses, fetchPendingDoses, updateDose, takeAllPetDoses } = useMedicationsStore()
  const { upcomingCheckups, fetchUpcomingCheckups } = useCheckupsStore()
  const { upcomingVaccines, fetchUpcomingVaccines } = useVaccinesStore()
  const { summaries, recentDoses, sortOption, fetchDashboardSummaries, fetchRecentDoses, setSortOption } = useDashboardStore()
  const isDesktop = useIsDesktop()

  const [selectedPetId, setSelectedPetId] = useState<string | null>(null)

  useEffect(() => {
    // Fetch backend summaries (contains nextDose computed by backend)
    fetchDashboardSummaries()
    // Also fetch detailed data for expanded views
    fetchPets()
    fetchPendingDoses()
    fetchRecentDoses()
    fetchUpcomingCheckups()
    fetchUpcomingVaccines()
  }, [fetchDashboardSummaries, fetchPets, fetchPendingDoses, fetchRecentDoses, fetchUpcomingCheckups, fetchUpcomingVaccines])

  // For mobile/expanded views, combine data using backend summaries for urgency
  const petsWithActions = useMemo(
    () =>
      aggregatePetActions(
        pets,
        pendingDoses,
        recentDoses,
        upcomingCheckups,
        upcomingVaccines,
        sortOption,
        summaries // Pass backend summaries for urgency values
      ),
    [pets, pendingDoses, recentDoses, upcomingCheckups, upcomingVaccines, sortOption, summaries]
  )

  // Auto-select first pet on desktop if none selected
  useEffect(() => {
    const firstSummary = summaries[0]
    if (isDesktop && !selectedPetId && firstSummary) {
      setSelectedPetId(firstSummary.id)
    }
  }, [isDesktop, selectedPetId, summaries])

  const selectedPetWithActions = useMemo(
    () => petsWithActions.find((p) => p.pet.id === selectedPetId) || null,
    [petsWithActions, selectedPetId]
  )

  const handleDoseAction = async (doseId: string, status: DoseStatus, notes?: string) => {
    await updateDose(doseId, status, notes)
    fetchPendingDoses()
    fetchRecentDoses()
  }

  const handleTakeAllDoses = async (petId: string) => {
    const count = await takeAllPetDoses(petId)
    fetchPendingDoses()
    fetchRecentDoses()
    return count
  }

  if (!user) return null

  // Use summaries for count (backend-authoritative)
  const hasActivePets = summaries.length > 0 || pets.filter(p => p.status === 'active').length > 0

  const headerContent = (
    <div className="page-header">
      <h1>Hola, {user.nombre}</h1>
      {hasActivePets && (
        <div className="dashboard-sort">
          <label htmlFor="sortOption">Ordenar por:</label>
          <select
            id="sortOption"
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value as DashboardSortOption)}
            className="form-control form-control-sm"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  )

  const emptyContent = (
    <EmptyState
      title="Sin mascotas"
      description="Agrega tu primera mascota para comenzar a gestionar su cuidado"
      action={
        <Link to="/mascotas/nueva" className="btn btn-primary">
          Agregar mascota
        </Link>
      }
    />
  )

  return (
    <ResponsiveLayout>
      <div className="page dashboard-page">
        {headerContent}

        {!hasActivePets ? (
          emptyContent
        ) : isDesktop ? (
          <MasterDetailLayout
            hasSelection={!!selectedPetWithActions}
            list={
              <div className="dashboard-pet-list dashboard-pet-list--compact">
                {summaries.map((summary) => (
                  <PetListCard
                    key={summary.id}
                    summary={summary}
                    isSelected={selectedPetId === summary.id}
                    onSelect={() => setSelectedPetId(summary.id)}
                  />
                ))}
              </div>
            }
            detail={
              selectedPetWithActions && (
                <PetDetailPanel
                  petWithActions={selectedPetWithActions}
                  onDoseAction={handleDoseAction}
                  onTakeAllDoses={handleTakeAllDoses}
                />
              )
            }
          />
        ) : (
          <ExpandableCardList accordion>
            {petsWithActions.map((petWithActions) => (
              <PetTaskCard
                key={petWithActions.pet.id}
                id={petWithActions.pet.id}
                petWithActions={petWithActions}
                onDoseAction={handleDoseAction}
              />
            ))}
          </ExpandableCardList>
        )}
      </div>
    </ResponsiveLayout>
  )
}
